/**
 * Runtime configuration loader.
 * Reads configuration from window.__RUNTIME_CONFIG__ which is set at runtime.
 * This allows configuration without rebuilding the image.
 */

interface RuntimeConfig {
  API_URL: string;
  WDS_SOCKET_HOST?: string;
  WDS_SOCKET_PORT?: string;
  WDS_SOCKET_PATH?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Get runtime configuration value.
 * Derives API_URL from window.location if not provided in runtime config.
 */
export function getRuntimeConfig(): RuntimeConfig {
  // If runtime config exists, use it (for custom overrides)
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    return window.__RUNTIME_CONFIG__;
  }

  // Otherwise, derive from window.location (browser knows its own URL)
  // Note: API_URL should NOT include /api suffix because API paths already include it
  // (e.g., /api/auth/login). The axios baseURL + path will combine correctly.
  if (typeof window !== 'undefined') {
    return {
      API_URL: window.location.origin,
    };
  }

  throw new Error('Runtime configuration not available. This should not happen in a browser environment.');
}

/**
 * Get API URL from runtime config or derive from window.location.
 */
export function getApiUrl(): string {
  const config = getRuntimeConfig();
  const apiUrl = config?.API_URL;
  if (!apiUrl) {
    // Fallback if API_URL is somehow missing
    if (typeof window !== 'undefined' && window.location) {
      return window.location.origin;
    }
    // Last resort fallback (should not happen in browser)
    return 'http://localhost:8000';
  }
  return apiUrl;
}

